import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import ChartCard from "../components/ChartCard";
import type { AppDataState } from "../api/useAppData";
import { ownerNames } from "../../shared/owners";

const METRICS = [
  { key: "teamsRemaining", label: "Teams remaining" },
  { key: "goalDifference", label: "Goal difference" },
  { key: "goalsFor", label: "Goals scored" },
  { key: "points", label: "Official points" },
  { key: "rank", label: "Rank" },
] as const;

type MetricKey = (typeof METRICS)[number]["key"];

export default function StatsPage({ state }: { state: AppDataState }) {
  const { data } = state;
  const [params, setParams] = useSearchParams();
  const focused = (params.get("metric") as MetricKey | null) ?? null;

  const [visible, setVisible] = useState<Set<string>>(
    new Set(ownerNames)
  );

  const charts = useMemo(() => {
    if (focused && METRICS.some((m) => m.key === focused)) {
      return METRICS.filter((m) => m.key === focused);
    }
    return METRICS;
  }, [focused]);

  if (!data) return <p>Loading…</p>;

  return (
    <>
      <h1>Stats</h1>

      <div className="chart-toolbar" role="group" aria-label="Visible owners">
        {ownerNames.map((o) => {
          const active = visible.has(o);
          return (
            <button
              key={o}
              type="button"
              className={`chip${active ? " active" : ""}`}
              onClick={() => {
                const next = new Set(visible);
                if (active) next.delete(o);
                else next.add(o);
                setVisible(next);
              }}
              aria-pressed={active}
            >
              {o}
            </button>
          );
        })}
      </div>

      <div className="chart-toolbar" role="group" aria-label="Focus metric">
        <button
          type="button"
          className={`chip${!focused ? " active" : ""}`}
          onClick={() => setParams({})}
        >
          All charts
        </button>
        {METRICS.map((m) => (
          <button
            key={m.key}
            type="button"
            className={`chip${focused === m.key ? " active" : ""}`}
            onClick={() => setParams({ metric: m.key })}
          >
            {m.label}
          </button>
        ))}
      </div>

      {charts.map((m) => (
        <ChartCard
          key={m.key}
          title={m.label + " over time"}
          metric={m.key}
          history={data.chartHistory}
          visibleOwners={visible}
          reverseY={m.key === "rank"}
          fixtures={data.allFixtures}
        />
      ))}

      <ExtraStatsCard data={data} />
    </>
  );
}

function ExtraStatsCard({
  data,
}: {
  data: NonNullable<AppDataState["data"]>;
}) {
  const e = data.extraStats;
  const items: { label: string; value: string }[] = [];
  if (e.bestTeamOwned) {
    items.push({
      label: "Best team owned",
      value: `${e.bestTeamOwned.team} (${e.bestTeamOwned.owner}) ${formatSigned(
        e.bestTeamOwned.goalDifference
      )} GD`,
    });
  }
  if (e.worstTeamOwned) {
    items.push({
      label: "Worst team owned",
      value: `${e.worstTeamOwned.team} (${e.worstTeamOwned.owner}) ${formatSigned(
        e.worstTeamOwned.goalDifference
      )} GD`,
    });
  }
  if (e.cleanestDefence) {
    items.push({
      label: "Cleanest defence",
      value: `${e.cleanestDefence.team} (${e.cleanestDefence.owner}) — ${e.cleanestDefence.goalsAgainst} conceded`,
    });
  }
  if (e.mostGoalsConceded) {
    items.push({
      label: "Most conceded",
      value: `${e.mostGoalsConceded.team} (${e.mostGoalsConceded.owner}) — ${e.mostGoalsConceded.goalsAgainst} conceded`,
    });
  }
  if (e.mostBrutalDefeat) {
    items.push({
      label: "Most brutal defeat",
      value: `${e.mostBrutalDefeat.homeTeam} ${e.mostBrutalDefeat.homeGoals}–${e.mostBrutalDefeat.awayGoals} ${e.mostBrutalDefeat.awayTeam}`,
    });
  }
  if (e.mostGoalsInOneMatch) {
    items.push({
      label: "Most goals in a match",
      value: `${e.mostGoalsInOneMatch.homeTeam} ${e.mostGoalsInOneMatch.homeGoals}–${e.mostGoalsInOneMatch.awayGoals} ${e.mostGoalsInOneMatch.awayTeam}`,
    });
  }
  if (e.perfectRecordTeams.length) {
    items.push({
      label: "Perfect record",
      value: e.perfectRecordTeams
        .map((t) => `${t.team} (${t.owner})`)
        .join(", "),
    });
  }

  if (items.length === 0) {
    return (
      <section className="section card">
        <h2>Extra stats</h2>
        <div className="empty-card">Stats will appear after matches finish.</div>
      </section>
    );
  }

  return (
    <section className="section card">
      <h2>Extra stats</h2>
      <ul className="mini-stats-list">
        {items.map((it) => (
          <li key={it.label}>
            <span className="label">{it.label}:</span>
            {it.value}
          </li>
        ))}
      </ul>

      {e.ownerVsOwnerRecords.length ? (
        <>
          <h2 style={{ marginTop: 16 }}>Owner v owner record</h2>
          <table className="standings-table">
            <thead>
              <tr>
                <th className="team-cell">Pair</th>
                <th>Wins</th>
                <th>Draws</th>
                <th>Losses</th>
              </tr>
            </thead>
            <tbody>
              {e.ownerVsOwnerRecords
                .filter((r) => r.ownerAWins + r.ownerBWins + r.draws > 0)
                .map((r) => (
                  <tr key={`${r.ownerA}-${r.ownerB}`}>
                    <td className="team-cell">
                      {r.ownerA} v {r.ownerB}
                    </td>
                    <td>
                      {r.ownerAWins} – {r.ownerBWins}
                    </td>
                    <td>{r.draws}</td>
                    <td>
                      {r.ownerBWins} – {r.ownerAWins}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </>
      ) : null}
    </section>
  );
}

function formatSigned(n: number): string {
  if (n > 0) return `+${n}`;
  return String(n);
}
